import { parseISO, isBefore } from 'date-fns'
import moment from 'moment-timezone'
import type React from 'react'
import { useContext, useState } from 'react'
import styled from 'styled-components'

import Button from '@/components/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import TextAreaInput from '@/components/input/TextAreaInput'
import ctx from '@/context/AppContext'
import { graphQLQuery } from '@/hooks/hooks'
import QUERIES from '@/lib/graphql'
import type { Application } from '@/types/Activity'
import type { BREAKDOWN_VIEWS } from '@/types/Misc'
import type { Touchpoint } from '@/types/Touchpoints'
import { Colours } from '@/utils/styles'

import TouchpointMessage from './TouchpointMessage'

moment.locale('en-GB')

interface Props {
  isOpen: boolean
  onClose: () => void
  touchpoints: Touchpoint[]
  activeEntity: BREAKDOWN_VIEWS | null
  application: Application
  fetchSubjectTouchpoints: (application: Application) => void
  disableLeaveComment?: boolean
}

const Title = styled.h1`
  text-transform: capitalize;
`

const MessagesContainer = styled.div`
  overflow-y: auto;
  &::before {
    content: '';
    position: absolute;
    width: 6px;
    background-color: ${Colours.teal};
    height: 100%;
    left: 50px;
    margin-left: -3px;
  }
`

const Body = styled.div`
  height: 100%;
  display: grid;
  grid-gap: 20px 0;
  grid-template-rows: min-content 1fr;

  #sendbox {
    background: #fff;
    position: relative;
    height: 100%;
    bottom: 0;
  }
`

const StyledTextAreaInput = styled(TextAreaInput)`
  height: 250px;
`

const CommentsSidebar: React.FC<Props> = ({
  isOpen,
  onClose,
  touchpoints,
  activeEntity,
  application,
  fetchSubjectTouchpoints,
  disableLeaveComment = false,
}) => {
  const { personId, token } = useContext(ctx)

  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [message, setMessage] = useState('')

  async function deleteTouchpoint(id: string) {
    const res = await graphQLQuery({
      query: QUERIES.deleteTouchpoint,
      variables: {
        id,
      },
      customHeaders: {
        Authorization: `Bearer ${token}`,
      },
    })
    await res.json()
    fetchSubjectTouchpoints(application)
  }

  async function postNewSubjectSectionTouchpoint(message: string) {
    const { customerId, applicationId, subjectId } = application
    graphQLQuery({
      query: QUERIES.addTouchpoint,
      variables: {
        touchpoint: {
          customerId: customerId,
          applicationId: applicationId,
          subjectId: subjectId,
          type: 'subject-section',
          category: activeEntity?.toLowerCase(),
          message,
          author: {
            id: personId,
            type: 'person',
          },
        },
      },
      customHeaders: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(resData => {
        return resData.json()
      })
      .then(({ data }) => {
        if (data && data.addTouchpoint) {
          fetchSubjectTouchpoints(application)
          // Scroll to include the new message in the view
          const msgBoxContainer = document.getElementById('messages-container')
          if (msgBoxContainer) {
            msgBoxContainer.scrollTop = msgBoxContainer.scrollHeight
          }
        }
      })
      .catch(err => {
        console.log(err)
      })
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (message.trim().length === 0) {
      return
    }

    postNewSubjectSectionTouchpoint(message)
    setMessage('')
    setIsEditing(false)
  }

  const filteredTouchpoints = touchpoints.filter(
    ({ category }) => category.toLowerCase() === activeEntity?.toLowerCase(),
  )

  if (activeEntity === null || !touchpoints) return null
  return isOpen ? (
    <SlideInSidebar
      onClose={() => onClose()}
      header={
        <>
          <Title>{activeEntity.toLowerCase()} Updates</Title>{' '}
          <p>
            These comments will be visible to the agent within the Goodlord dashboard.
          </p>
          {disableLeaveComment && (
            <i>
              <p>
                <strong>Please Note: </strong>The ability to leave comments for agents is
                unsupported for this section.
              </p>
            </i>
          )}
        </>
      }
      content={
        <Body>
          {isEditing ? (
            <div id="sendbox">
              <form onSubmit={(e: React.FormEvent<HTMLFormElement>) => handleSubmit(e)}>
                <StyledTextAreaInput
                  label="Post a new update..."
                  value={message}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setMessage(e.target.value)
                  }
                />
                <div className="side-by-side-flex-end">
                  <Button $alternate onClick={() => setIsEditing(false)}>
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      message.trim() == '' // eslint-disable-line eqeqeq
                    }
                  >
                    Post
                  </Button>
                </div>
              </form>
            </div>
          ) : (
            <Button
              type="submit"
              onClick={() => setIsEditing(true)}
              disabled={disableLeaveComment}
            >
              Leave a comment
            </Button>
          )}
          {filteredTouchpoints.length > 0 && (
            <MessagesContainer>
              {filteredTouchpoints
                .map(touchpoint => ({
                  ...touchpoint,
                  createdAt: parseISO(touchpoint.createdAt),
                }))
                .sort(({ createdAt: createdAtA }, { createdAt: createdAtB }) =>
                  isBefore(createdAtA, createdAtB) ? 1 : -1,
                )
                .map((touchpoint, index) => (
                  <TouchpointMessage
                    touchpoint={touchpoint as unknown as Touchpoint}
                    deleteTouchpoint={(id: string) => deleteTouchpoint(id)}
                    index={index}
                    personId={personId ?? ''}
                  />
                ))}
            </MessagesContainer>
          )}
          {filteredTouchpoints.length === 0 && (
            <div>
              <p>Nothing here yet! Why not leave the first comment?</p>
            </div>
          )}
        </Body>
      }
    />
  ) : (
    <></>
  )
}

export default CommentsSidebar
